import React, { useEffect, useRef } from 'react';

// Turnstile Site Key - 从环境变量读取，fallback 到默认值
const TURNSTILE_SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACarCweSVGaOkQWt';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileProps {
  onVerify: () => void;
  onExpire?: () => void;
  onError?: () => void;
  onUnsupported?: () => void;
  timeout?: number;
}

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks: (() => void)[] = [];

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (scriptLoaded && window.turnstile) {
      resolve();
      return;
    }

    loadCallbacks.push(resolve);

    if (scriptLoading) return;
    scriptLoading = true;

    window.onTurnstileLoad = () => {
      scriptLoaded = true;
      scriptLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      scriptLoading = false;
      reject(new Error('Failed to load Turnstile script'));
    };
    document.head.appendChild(script);
  });
}

const Turnstile: React.FC<TurnstileProps> = ({ onVerify, onExpire, onError, onUnsupported, timeout = 8000 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const resolvedRef = useRef(false);
  // 用 ref 持有最新的回调，避免 useEffect 依赖变化导致重跑
  const callbacksRef = useRef({ onVerify, onExpire, onError, onUnsupported });
  callbacksRef.current = { onVerify, onExpire, onError, onUnsupported };

  useEffect(() => {
    let mounted = true;

    const timeoutId = setTimeout(() => {
      if (mounted && !resolvedRef.current) {
        resolvedRef.current = true;
        console.warn('[Turnstile] 加载超时，跳过验证');
        callbacksRef.current.onUnsupported?.();
      }
    }, timeout);

    loadTurnstileScript().then(() => {
      if (!mounted || !containerRef.current || !window.turnstile) {
        if (mounted && !resolvedRef.current) {
          resolvedRef.current = true;
          clearTimeout(timeoutId);
          callbacksRef.current.onUnsupported?.();
        }
        return;
      }

      if (widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: () => {
          if (!resolvedRef.current) {
            resolvedRef.current = true;
            clearTimeout(timeoutId);
            callbacksRef.current.onVerify();
          }
        },
        'expired-callback': () => {
          resolvedRef.current = false;
          callbacksRef.current.onExpire?.();
        },
        'error-callback': () => {
          if (!resolvedRef.current) {
            resolvedRef.current = true;
            clearTimeout(timeoutId);
            callbacksRef.current.onUnsupported?.();
          }
        },
        theme: 'light',
        language: 'zh-cn',
      });
    }).catch(() => {
      if (mounted && !resolvedRef.current) {
        resolvedRef.current = true;
        clearTimeout(timeoutId);
        callbacksRef.current.onUnsupported?.();
      }
    });

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }} />;
};

export default Turnstile;
